import {
  useEffect,
  useRef,
  useState,
  useMemo,
  useContext,
  useCallback,
  forwardRef,
} from 'react';
import { createPortal } from 'react-dom';
import { VariableSizeList as List } from 'react-window';
import { useComponentPosition } from 'hooks/useComponentPosition';
import {
  Label,
  SelectWrapper,
  ErrorMessage,
  SelectControl,
  SelectInput,
  SelectInputContainer,
  SelectIconContainer,
} from './SelectStyles';
import './select-styles.css';
import Tooltip from 'components/Tooltip/Tooltip';
import { AccordionArrow } from 'components/Accordion/AccordionStyles';
import { ArrowDown } from 'components/Icons';
import { GenericOption } from './Options';
import { DropdownLoader } from './DropdownLoader/DropdownLoader';
import { NavKey, zIndex } from 'helpers/constants';
import { WrapperContext } from 'components/Wrapper/Wrapper';

type Props = {
  options: any;
  value: any;
  placeholder?: string;
  isTree?: boolean;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  width?: string;
  withCheckboxes?: boolean;
  hasError?: boolean;
  onChange: (e: any, selectedItem?: any) => void;
  ref?: any;
  errorMessage?: string;
  errorProps?: any;
  testid: string;
  isLoading?: boolean;
  showTooltip?: any;
  optionHeight?: number;
  variant?: 'table' | 'default';
  split?: boolean;
  noOptionsLabel?: string;
  Option?: (props: any) => React.FC<any>;
  style?: any;
};

export const dropdownOptionClassName = 'ReactWindowListItem';

const optionsDefaultHeight = 300;
const keyDownHandling = [
  NavKey.down,
  NavKey.up,
  NavKey.pageDown,
  NavKey.pageUp,
  NavKey.enter,
];
const keyPageSize = 7;

enum InputState {
  set = 'set',
  edit = 'edit',
  default = '',
}

export const WindowSelect = ({
  placeholder = 'Search for component',
  testid,
  options = [],
  onChange,
  Option = GenericOption,
  value = {
    label: '',
  },
  noOptionsLabel = 'No options',
  hasError,
  width = '250px',
  disabled,
  isLoading,
  label,
  required,
  errorMessage,
  optionHeight = 40,
  errorProps,
  style,
  variant = 'default',
}: Props) => {
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [inputState, setInputState] = useState<InputState>(InputState.default);
  const listRef = useRef<List>(null);
  const dropdownWrapperRef = useRef(null);
  const { wrapperState, setWrapperState } = useContext(WrapperContext);
  const inputRef = useRef<HTMLInputElement>(null);
  const isOpened = wrapperState[testid] && !disabled;

  const filterOptions = (input: string) => {
    const filtered = options.filter((option: any) =>
      option.label.toLowerCase().includes(input.toLowerCase())
    );
    if (!filtered.length) {
      setFilteredOptions([{ value: null, label: noOptionsLabel }]);
      return;
    }

    setFilteredOptions(filtered);
  };

  const resetDropdown = () => {
    resetListCache();
    if (inputRef.current) {
      if (inputRef.current?.value !== value?.label) {
        setFilteredOptions(options);
        inputRef.current.value = value?.label;
      }
    }
  };

  const resetListCache = useCallback(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0);
    }
  }, []);

  const handleSelect = (option: any) => {
    setWrapperState({});
    resetListCache();
    setFilteredOptions(options);
    // @ts-ignore
    inputRef.current.value = option.label;
    setInputState(InputState.set);
    onChange(option);
  };
  const ListOption = Option({
    handleSelect,
    selected: value,
  });
  const handleInput = (e: any) => {
    resetListCache();
    if (inputRef.current) {
      if (inputState === InputState.set) {
        setInputState(InputState.edit);
        const newValue = e.nativeEvent.data || '';
        inputRef.current.value = newValue;
        filterOptions(newValue);
        return;
      }
      filterOptions(inputRef.current.value);
    }
  };

  const optionsHeight = useMemo(
    () =>
      filteredOptions.length > optionsDefaultHeight / optionHeight
        ? optionsDefaultHeight
        : filteredOptions.length * optionHeight,
    [filteredOptions.length, optionHeight]
  );

  // TODO: move to separate component
  const { position, isBottomTop } = useComponentPosition({
    isOpened,
    componentRef: dropdownWrapperRef,
    componentHeight: optionsHeight,
  });

  // pass custom attributes to a react-window component
  const outerElementType = useMemo(
    () =>
      forwardRef((props, ref: any) => {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: isBottomTop ? 'flex-end' : 'flex-start',
              position: 'absolute',
              ...position,
            }}
          >
            <div ref={ref} data-testid={testid} {...props} />
          </div>
        );
      }),
    [testid, position]
  );

  useEffect(() => {
    if (disabled) {
      if (inputRef.current) {
        inputRef.current.value = '';
        setInputState(InputState.default);
      }
      return;
    }

    if (value?.label && inputRef.current) {
      inputRef.current.value = value.label;
      if (inputState === InputState.default) {
        setInputState(InputState.set);
      }
    }
  }, [value, disabled, inputRef]);
  useEffect(() => {
    if (options) {
      resetListCache();
      setFilteredOptions(options);
    }
  }, [options]);
  // onClose cleanup
  useEffect(() => {
    if (!wrapperState[testid]) {
      resetDropdown();
      setInputState(InputState.set);
    }
  }, [wrapperState]);

  function handleKeyDown(event: any) {
    if (keyDownHandling.includes(event.key)) {
      event.stopPropagation();
      event.preventDefault();

      if (event.key === NavKey.enter) {
        setWrapperState({});
      }

      let selectedIndex = -1;
      if (value.value) {
        selectedIndex = filteredOptions.findIndex(
          (element: any) => element.value === value.value
        );
      }
      switch (event.key) {
        case NavKey.down:
          if (selectedIndex < options.length - 1) {
            selectedIndex++;
          }
          break;
        case NavKey.up:
          if (selectedIndex > 0) {
            selectedIndex--;
          } else {
            selectedIndex = 0;
          }
          break;
        case NavKey.pageDown:
          if (selectedIndex < options.length - keyPageSize) {
            selectedIndex += keyPageSize;
          } else {
            selectedIndex = options.length - 1;
          }
          break;
        case NavKey.pageUp:
          if (selectedIndex > keyPageSize) {
            selectedIndex -= keyPageSize;
          } else {
            selectedIndex = 0;
          }
          break;
      }

      if (selectedIndex >= 0) {
        onChange(filteredOptions[selectedIndex]);
        listRef.current?.scrollToItem(selectedIndex);
      }
    }
  }

  const handlePaste = (event: any) => {
    if (inputRef.current) {
      event.preventDefault();
      // @ts-ignore
      const clipboardData = event.clipboardData || window.clipboardData;
      const pastedText = clipboardData.getData('text');
      inputRef.current.value = pastedText;
      filterOptions(pastedText);
    }
  };

  return (
    <SelectWrapper
      ref={dropdownWrapperRef}
      style={style}
      width={width}
      tabIndex={1}
      onKeyDown={handleKeyDown}
      key={testid}
    >
      {label && (
        <Label>
          {required && <span>*</span>}
          {label}
        </Label>
      )}
      <Tooltip
        // message={!wrapperState[testid] ? value.label : null}
        message=''
        delay={300}
        followCursor={false}
        placement='top'
      >
        <SelectControl
          variant={variant}
          hasError={hasError}
          isDisabled={disabled}
          data-isselect={true}
          onClick={(e: any) => {
            if (isLoading && !options.length) return;
            setWrapperState({ [testid]: !isOpened });
            if (!isOpened && inputRef.current) {
              inputRef.current.focus();
              inputRef.current.setSelectionRange(0, 0);
              inputRef.current.style.caretColor = 'auto';
            }
            e.stopPropagation();
          }}
        >
          <SelectInputContainer>
            <SelectInput
              onPaste={handlePaste}
              value={inputRef.current ? inputRef.current.value : value?.label}
              disabled={disabled}
              placeholder={placeholder}
              ref={inputRef}
              onInput={handleInput}
            />
            {isLoading && <DropdownLoader />}
            <SelectIconContainer>
              <AccordionArrow data-testid='expandArrow'>
                <ArrowDown
                  size='8px'
                  fill='rgb(135, 149, 162)'
                  transform='scale(1.1, 0.9)'
                />
              </AccordionArrow>
            </SelectIconContainer>
          </SelectInputContainer>
          {isOpened &&
            createPortal(
              <List
                ref={listRef}
                outerElementType={outerElementType}
                style={{
                  display: 'flex',
                  position: 'absolute',
                  backgroundColor: 'white',
                  margin: '8px 0 0 0',
                  borderRadius: '3px',
                  border: '1px solid #d9dddd',
                  zIndex: zIndex.menuListActive,
                }}
                height={optionsHeight}
                data-testid={testid}
                itemData={filteredOptions}
                itemCount={filteredOptions.length}
                itemSize={(index: number) => {
                  const nameLength = filteredOptions[index]
                    ? filteredOptions[index]?.label.length
                    : 0;
                  const res = Math.ceil(nameLength / 20) * 15; // approx. 20 characters in line
                  return res > 30 ? res : 30;
                }}
                width={width}
              >
                {ListOption}
              </List>,
              document.body
            )}
        </SelectControl>
      </Tooltip>
      {errorMessage && (
        <ErrorMessage {...errorProps}>{errorMessage}</ErrorMessage>
      )}
    </SelectWrapper>
  );
};
